var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useQueries, useQuery } from "@tanstack/react-query";
import { getKpiCardVisuals } from "../../../components/dashboard/kpi-icons/kpiIconRegistry";
import { formatNumericValue, getChartDataset, parseNumericValue, resolveLabelAndValueKeys, } from "../../../utils/chartData";
import { normalizeDisplayText } from "../../../utils/displayText";
import { DISQUE100_SUBTAB_ID_BY_TAB, isPeriodFilterActive, } from "../config/tabs";
import { disque100QueryKeys } from "../query-keys/disque100.query-keys";
import { useDisque100Filters } from "./useDisque100Filters";
import { fetchDisque100ChartData, fetchDisque100Kpis, } from "../services/disque100.service";
var buildLoadingChart = function (chart) { return ({
    id: chart.id,
    title: chart.title,
    subtitle: chart.subtitle,
    chartType: chart.chartType,
}); };
function mapChartDefinitionToState(chart) {
    return {
        id: chart.id,
        title: chart.title,
        subtitle: chart.subtitle,
        chartType: chart.chartType,
        endpoint: chart.endpoint,
    };
}
function extractKpiValue(data, extract, suffix) {
    var dataset = getChartDataset(data);
    var _a = resolveLabelAndValueKeys(data, dataset), labelKey = _a.labelKey, valueKey = _a.valueKey;
    var items = dataset.map(function (item) {
        var _a, _b, _c;
        return ({
            label: String((_a = item[labelKey]) !== null && _a !== void 0 ? _a : ""),
            value: parseNumericValue((_c = (_b = item[valueKey]) !== null && _b !== void 0 ? _b : item.value) !== null && _c !== void 0 ? _c : 0),
        });
    });
    if (items.length === 0)
        return { value: "—", detail: "sem dados" };
    if (extract === "total") {
        var total = items.reduce(function (acc, i) { return acc + i.value; }, 0);
        return {
            value: formatNumericValue(total),
            detail: "".concat(formatNumericValue(total), " ").concat(suffix),
        };
    }
    if (extract === "count") {
        return {
            value: formatNumericValue(items.length),
            detail: "".concat(formatNumericValue(items.length), " ").concat(suffix),
        };
    }
    var sorted = __spreadArray([], items, true).sort(function (a, b) {
        return extract === "max_entry" ? b.value - a.value : a.value - b.value;
    });
    var target = sorted[0];
    var formatLabel = function (s) {
        return /^\d{4}-\d{2}/.test(s) ? s.slice(0, 4) : s;
    };
    return {
        value: normalizeDisplayText(formatLabel(target.label)),
        detail: "".concat(formatNumericValue(target.value), " ").concat(suffix),
    };
}
function buildKpiCard(item) {
    var _a, _b;
    var visuals = getKpiCardVisuals(item.id);
    var display = item.payload
        ? extractKpiValue(item.payload, item.extract, (_a = item.detail_suffix) !== null && _a !== void 0 ? _a : "")
        : { value: "—", detail: (_b = item.error) !== null && _b !== void 0 ? _b : "sem dados" };
    return __assign(__assign({}, visuals), { label: item.label, value: display.value, detail: display.detail, loading: false });
}
export function useDisque100Tab(tab, dynamicSubtab) {
    var _a, _b, _c, _d;
    var _e = useDisque100Filters(), yearFilter = _e.yearFilter, semesterFilter = _e.semesterFilter;
    var filters = { yearFilter: yearFilter, semesterFilter: semesterFilter };
    var subtabId = (_a = dynamicSubtab === null || dynamicSubtab === void 0 ? void 0 : dynamicSubtab.slug) !== null && _a !== void 0 ? _a : DISQUE100_SUBTAB_ID_BY_TAB[tab];
    var periodFilterActive = isPeriodFilterActive(tab, yearFilter);
    var chartConfigs = ((_b = dynamicSubtab === null || dynamicSubtab === void 0 ? void 0 : dynamicSubtab.charts) !== null && _b !== void 0 ? _b : [])
        .filter(function (chart) {
        if (periodFilterActive) {
            return chart.id === "ranking_group_period";
        }
        return chart.id !== "ranking_group_period";
    })
        .map(mapChartDefinitionToState);
    var kpisEndpoint = dynamicSubtab === null || dynamicSubtab === void 0 ? void 0 : dynamicSubtab.kpisEndpoint;
    var chartResults = useQueries({
        queries: chartConfigs.map(function (chart) {
            var _a, _b;
            return ({
                queryKey: disque100QueryKeys.data((_b = (_a = chart.endpoint) !== null && _a !== void 0 ? _a : chart.id) !== null && _b !== void 0 ? _b : chart.title, filters),
                queryFn: function () { return fetchDisque100ChartData(chart.endpoint, filters); },
            });
        }),
    });
    var groupedKpisQuery = useQuery({
        queryKey: disque100QueryKeys.kpis(subtabId, filters),
        enabled: Boolean(kpisEndpoint),
        queryFn: function () { return fetchDisque100Kpis(kpisEndpoint, filters); },
    });
    var charts = chartConfigs.map(function (chart, index) {
        var result = chartResults[index];
        if (result === null || result === void 0 ? void 0 : result.data) {
            return __assign(__assign({}, buildLoadingChart(chart)), { data: result.data.data });
        }
        if (result === null || result === void 0 ? void 0 : result.error) {
            return __assign(__assign({}, buildLoadingChart(chart)), { error: result.error instanceof Error
                    ? result.error.message
                    : "Não foi possível obter os dados." });
        }
        return buildLoadingChart(chart);
    });
    var groupedKpisById = new Map(((_c = groupedKpisQuery.data) !== null && _c !== void 0 ? _c : []).map(function (item) { return [
        item.id,
        item,
    ]; }));
    var kpiCards = ((_d = groupedKpisQuery.data) !== null && _d !== void 0 ? _d : [])
        .filter(function (item) {
        var isPeriodKpi = item.id.endsWith(".period_total") ||
            item.id.endsWith(".period_top_group") ||
            item.id.endsWith(".period_bottom_group");
        if (periodFilterActive) {
            return isPeriodKpi;
        }
        return !isPeriodKpi;
    })
        .map(function (item) { var _a; return buildKpiCard((_a = groupedKpisById.get(item.id)) !== null && _a !== void 0 ? _a : item); });
    return { charts: charts, kpiCards: kpiCards, kpisLoading: groupedKpisQuery.isLoading };
}
